import React, { FC, memo, useCallback } from 'react';
import DialogActions from '@material-ui/core/DialogActions';
import DialogContent from '@material-ui/core/DialogContent';
import DialogTitle from '@material-ui/core/DialogTitle';
import MaterialDialog from '@material-ui/core/Dialog';
import { DialogProps as MuiDialogProps } from '@material-ui/core/Dialog/Dialog';
import styled from 'styled-components';
import { DefaultButton } from '../Button';
import { useTheme, useMediaQuery } from '../MaterialUtils';
import { Typography } from '../Typography';
import { SystemIconButton } from '../Icon/SystemIconButton';
import { Uds } from '../../types';
import { useIntlUds } from '../../translations/UdsIntlProvider';

export type DialogProps = Uds<{
  title?: string | JSX.Element;
  subTitle?: string | JSX.Element;
  open: boolean;
  onClose: () => void;
  actions?: JSX.Element[];
  /*! @default: false. Remove jumping scrollbar effect, when content has changed */
  stableScrollbar?: boolean;
  /*! @default: false */
  showCloseButton?: boolean;
  /*! @default: false */
  showCancelButton?: boolean;
  /*! @default: false */
  isFullContentHeight?: boolean;
  heightRem?: number;
  /*! @default: false */
  disabled?: boolean;
  /*! @default: false */
  fullScreen?: boolean;
  /*! @default: true */
  disableBackdropClick?: boolean;
  /*! @default: true
   * This value will set maxWidth to "sm" size from material-ui (600px)
   */
  useMaxWidth?: boolean;
  /*! @default: true */
  isMaxWidth?: boolean;
  responsive?: boolean;
  /*!
   * To set custom width use props combination below:
   *    widthRem={50} <== your size
   *    maxWidthRem="100%" <== fit content to available space
   *    useMaxWidth={false} <== reset default max width
   *    isMaxWidth={false} <== reset default max width
   */
  widthRem?: number;
  maxWidthRem?: number | '100%';
  scroll?: MuiDialogProps['scroll'];
  className?: string;
  id?: string;
  disableEnforceFocus?: boolean;
  disablePortal?: boolean;
}>;

export const Dialog: FC<React.PropsWithChildren<DialogProps>> = memo((props) => {
  const { intl } = useIntlUds();

  const {
    dataTest,
    onClose,
    actions,
    title,
    children,
    subTitle,
    open,
    stableScrollbar = false,
    widthRem,
    maxWidthRem,
    showCloseButton = false,
    showCancelButton = false,
    heightRem,
    disabled = false,
    disableBackdropClick = true,
    fullScreen = false,
    useMaxWidth = true,
    isMaxWidth = true,
    responsive,
    scroll = 'paper',
    isFullContentHeight = false,
    className,
    disableEnforceFocus,
    disablePortal,
    id
  } = props;

  const closeHandler = useCallback(() => {
    if (!disabled) {
      onClose();
    }
  }, [onClose, disabled]);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const fullScreenOnMobile = responsive && isMobile;
  const actionsVisible = showCancelButton || actions?.length;
  return (
    <MaterialDialog
      className={className}
      open={open}
      onClose={(_, reason) => {
        if (!disableBackdropClick) return closeHandler();
        if (reason !== 'backdropClick') {
          closeHandler();
        }
        return undefined;
      }}
      data-test={dataTest || 'mui-dialog'}
      maxWidth={useMaxWidth ? 'sm' : false}
      fullWidth={isMaxWidth}
      fullScreen={fullScreen || fullScreenOnMobile}
      scroll={scroll}
      id={id}
      disableEnforceFocus={disableEnforceFocus}
      disablePortal={disablePortal}
    >
      {title || showCloseButton || subTitle ? (
        <DialogTitle disableTypography id="customized-dialog-title">
          {title && (
            <Typography dataTest="modal__title" variant="h4">
              {title}
            </Typography>
          )}
          {showCloseButton && (
            <StyledIconButton
              iconName="close"
              aria-label="close"
              disabled={disabled}
              onClick={closeHandler}
              dataTest="dialog_close__button"
            />
          )}
          {!!subTitle && (
            <StyledSubTitle data-test="modal__subtitle">
              <Typography variant="body1" color="inherit">
                {subTitle}
              </Typography>
            </StyledSubTitle>
          )}
        </DialogTitle>
      ) : null}
      {children && (
        <DialogContent
          style={{
            scrollbarGutter: stableScrollbar ? 'stable' : 'auto',
            width: px(widthRem),
            height: px(heightRem),
            maxWidth: px(maxWidthRem)
          }}
          data-test="modal__content"
        >
          <StyledContentContainer data-test="content_container" $isFullContentHeight={isFullContentHeight}>
            {children}
          </StyledContentContainer>
        </DialogContent>
      )}
      {disabled && <StyledBlockingScrim data-test="dialog__blocking__scrim" />}
      {actionsVisible && (
        <DialogActions>
          {showCancelButton && (
            <DefaultButton.Text
              dataTest="dialog-cancel-button"
              className="btn"
              onClick={onClose}
              style={{ minWidth: 75 }}
            >
              {intl.Dialog.Dialog.cancelLabel}
            </DefaultButton.Text>
          )}
          {/* eslint-disable-next-line react/jsx-no-useless-fragment */}
          {!!actions?.length && actions}
        </DialogActions>
      )}
    </MaterialDialog>
  );
});

Dialog.displayName = 'Dialog';

export default Dialog;

interface StyledContentContainerProps {
  $isFullContentHeight: boolean;
}

const StyledContentContainer = styled.div<StyledContentContainerProps>`
  ${(props) =>
    props.$isFullContentHeight &&
    `
    height: 100%;
    display: flex;
    flex-flow: column;
  `}
`;

const StyledSubTitle = styled.div`
  padding-top: ${({ theme }) => theme.spacingSize.m};
`;

const StyledIconButton = styled(SystemIconButton)`
  && {
    position: absolute;
    top: ${({ theme }) => theme.spacingSize.m};
    right: ${({ theme }) => theme.spacingSize.m};
  }
`;

const StyledBlockingScrim = styled.div`
  opacity: 0.1;
  background-color: black;
  width: 100%;
  height: 100%;
  position: absolute;
  z-index: 1000;
`;

function px(rem?: number | '100%'): string | undefined {
  return typeof rem === 'number' ? `${rem * 16}px` : rem;
}
